import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { RouteComponentProps, useLocation } from "react-router-dom";
import ReactGA from "react-ga4";
import { animated } from "react-spring";
import { SyncOutlined } from "src/icons";
import styles from "./index.module.scss";
import PinnedContainer from "src/shared/components/India2MIS/PinnedContainer";
import TopNavbar, { MenuItem } from "src/shared/components/Navbar/NewNavbar";
import { fetchHome, searchPin, searchReset, FetchHomePayload, SearchPinPayload } from "src/store/home";
import { fetchAllOrgMetrics, FetchAllOrgMetricsPayload } from "src/store/allOrg";
import { setCategoriesFilters, categoryReset } from "src/store/category";
import { setFilters, clearDetails } from "src/store/details";
import { changeTimeTabAppWide, UserRoles, setIsRoleCalloutShownAction } from "src/store/app";
import { logoutAction } from "src/store/login";
import RibbonSlider from "src/shared/components/RibbonSlider";
import { useRedux } from "src/shared/hooks";
import ManageMetrics from "./ManageMetric";
import Search from "src/shared/components/Search";
import { IRootState, IconClass } from "src/shared/types";
import { IReduxOperations, IActionFactory } from "src/store";
import { SearchTarget } from "src/shared/types";
import useDragger, { DRAGPANEL_TOP_THRESHOLD } from "./useDragger/index2";
import styled from "@emotion/styled";
import userService from "src/shared/services/user.service";
import colors from "src/styles/variables.scss";
import { useEffectOnPropsChange, checkIfIOSDevice, noop, safeGet } from "src/shared/utils";
import Permissions from "../../AllOrg/Permissions";
import { setComingBackFromDetailsPage } from "src/store/app";
import FilterByPod from "./FilterbyPod";
import { devLogging } from "src/utils/logger";
import { getCityOptions, getPodOptions } from "src/utils/getFilterOptions";
import { RunningAnobisMode } from "src/shared/config";
import { Button, Select, Modal } from "antd";
import { getServiceName, ServiceName } from "src/utils/getservicename";
import { FilterOutlined, FilterFilled } from "@ant-design/icons";
import { fetchRestaurantData, fetchPocData, fetchL1ManagerData } from "src/utils/restaurantUtils";
import { isL2 } from "src/utils/misRoleUtils";
import { Filter } from "src/shared/models/ManageMetricsModel";

const CALL_DELAY = 1000;

interface IState {
  metrics: IReduxOperations;
  search: IReduxOperations;
  selectedTab: any;
  userRoles: IReduxOperations<UserRoles>;
  isRoleCalloutShown: boolean;
}

type MappedActionProps = {
  fetchHome(payload: FetchHomePayload);
  searchPin(payload: SearchPinPayload);
  setCategoriesFilters: IActionFactory<symbol, IObject<any>>;
  categoryReset: IActionFactory<symbol, unknown>;
  searchReset: IActionFactory<symbol, unknown>;
  logoutAction();
  setFilters: IActionFactory<symbol, IObject<any>>;
  clearDetails: IActionFactory<symbol, any>;
  changeTimeTabAppWide: IActionFactory<symbol, string>;
  setIsRoleCalloutShown: IActionFactory<symbol, boolean>;
  fetchAllOrgMetrics(payload?: FetchAllOrgMetricsPayload);
  setComingBackFromDetailsPage: IActionFactory<symbol, boolean>;
};

const SliderContainer = styled.article`
  position: absolute;
  z-index: 1;
  width: inherit;
  height: 100vh;
  background: ${colors.blackBackground};
`;

const DragPanel = styled.section<{ translateYVal?: number }>`
  transition: ${checkIfIOSDevice() ? "none" : "transform 0.2s cubic-bezier(0.175, 0.985, 0.32, 1.275)"};
  -moz-transition: none;
`;

const SyncIcon = styled(SyncOutlined)`
  font-size: 1.5rem;
  height: min-content;
  width: 1.5rem;
  line-height: 15px;
`;

const ROLE_CALLOUT_SHOWN = "ROLE_CALLOUT_SHOWN";

// Add styled components for chips
const FilterChipsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 0px;
  padding: 0 16px;
`;

const FilterChip = styled.div`
  background-color: ${colors.primaryColor}15;
  color: ${colors.primaryColor};
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

// Add styled component for loading overlay
const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.29);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
  flex-direction: column;
`;

const LoadingSpinner = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  
  &:before, &:after {
    content: '';
    position: absolute;
    border-radius: 50%;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  
  &:before {
    border: 4px solid rgba(0, 0, 0, 0.1);
  }
  
  &:after {
    border: 4px solid transparent;
    border-top-color: ${colors.primaryColor};
    animation: spin 1.2s linear infinite;
  }
  
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const InnerSpinner = styled.div`
  position: absolute;
  width: 50%;
  height: 50%;
  border-radius: 50%;
  border: 3px solid transparent;
  border-right-color: ${colors.primaryColor}AA;
  border-left-color: ${colors.primaryColor}AA;
  animation: spin-reverse 1s linear infinite;
  
  @keyframes spin-reverse {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(-360deg);
    }
  }
`;

const CenterPoint = styled.div`
  position: absolute;
  width: 12px;
  height: 12px;
  background-color: ${colors.primaryColor};
  border-radius: 50%;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
`;

const PulseRing = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  animation: pulse 2s cubic-bezier(0.455, 0.03, 0.515, 0.955) infinite;
  
  @keyframes pulse {
    0% {
      transform: scale(0.85);
      opacity: 1;
      border: 4px solid ${colors.primaryColor}40; /* 25% opacity */
    }
    100% {
      transform: scale(1.3);
      opacity: 0;
      border: 2px solid ${colors.primaryColor}00; /* 0% opacity */
    }
  }
`;

const SpinnerContainer = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LoadingText = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: ${colors.primaryColor};
  margin-top: 20px;
`;

const MainContainer: React.FC<RouteComponentProps> = ({ history }) => {
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: "/india-2-app", title: "india-2-app" });
    return () => {
      document.body.style.overflowY = "auto";
    };
  }, []);

  const [manageMetricsFlag, setManageMetricsFlag] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const dragPanel = useRef<HTMLDivElement>(null as any);
  const allOrgElement = React.useRef<HTMLDivElement>(null as any);
  const [page, setPage] = useState(1);
  const [targetSearch, setTargetSearch] = useState(SearchTarget.HOME);
  const [showSearch, toggleSearch] = useState(false);
  const [timeTabForImCom, setTimeTabForImCom] = useState("Today_india_2");
  const containerRef = useRef<HTMLDivElement>(null as any);

  const [selectedPod, setSelectedPod] = useState<string[] | null>(null);
  const [selectedCity, setSelectedCity] = useState<string[] | undefined>(undefined);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string[] | undefined>(undefined);
  const [selectedPocEmailId, setSelectedPocEmailId] = useState<string[] | undefined>(undefined);
  const [l1ManagerData, setL1ManagerData] = useState<string[]>([]);

  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const store_id_filter_value = queryParams.get("store_id");

  const showDetails = useCallback(
    (id, name) => {
      mappedProps.setComingBackFromDetailsPage(true);

      // Navigate to details page without query params since we're using localStorage
      history.push(`/india-2-app/details/pin/${id}`);

      ReactGA.event({
        category: "Pin Clicked on L1a",
        label: `Pin: ${name} on L1a`,
        action: "Click from L1a",
      });
    },
    [history]
  );

  const [showRibbon, setShowRibbon] = useState(false);

  useDragger({
    dragPanel,
    onPanStart(e: HammerInput, isDockedUp: boolean) {
      for (let childNode of dragPanel.current.childNodes) {
        if (childNode.nodeType === Node.ELEMENT_NODE) {
          (childNode as HTMLElement).style.pointerEvents = "none";
        }
      }
    },
    onPanEnd(e: HammerInput, isDockedUp: boolean) {
      setShowRibbon(isDockedUp);
      if (isDockedUp) {
        document.body.style.overflowY = "hidden";
      } else {
        document.body.style.overflowY = "auto";
      }
      for (let childNode of dragPanel.current.childNodes) {
        if (childNode.nodeType === Node.ELEMENT_NODE) {
          (childNode as HTMLElement).style.pointerEvents = "initial";
        }
      }
    },
    shouldPerformTouchGesture({ target: touchedTargetEl, direction }: HammerInput) {
      const sensitiveContainerEl = allOrgElement.current;
      if (sensitiveContainerEl) {
        const isTragetInsideSensitiveZone = sensitiveContainerEl.contains(touchedTargetEl);
        return !isTragetInsideSensitiveZone;
      }
      return true;
    },
  });

  const {
    setFilters: setL3Fiters,
    clearDetails: clearL3Data,
    ...mappedProps
  } = useRedux<IRootState, IState, MappedActionProps, {}>(
    (state) => ({
      metrics: state.home.pinnedMetrics.metrics,
      search: state.home.pinnedMetrics.search,
      loadMorePending: state.home.pinnedMetrics.loadMorePending,
      chartsDataPending: state.home.pinnedMetrics.chartsDataPending,
      selectedTab: state.app.selectedTab,
      comingBackFromDetailsPage: state.app.comingBackFromDetailsPage,
      userRoles: state.app.userRoles,
      isRoleCalloutShown: state.app.isRoleCalloutShown,
    }),
    {
      fetchHome,
      searchPin,
      searchReset,
      logoutAction,
      setCategoriesFilters,
      categoryReset,
      setFilters,
      clearDetails,
      changeTimeTabAppWide,
      setIsRoleCalloutShown: setIsRoleCalloutShownAction,
      fetchAllOrgMetrics,
      setComingBackFromDetailsPage,
    }
  );

  const onLogout = useCallback(() => {
    userService.logout();
    history.push("/");
    mappedProps.logoutAction();
  }, []);

  const [isRefreshClicked, setIsRefreshClicked] = useState(0);
  const [shouldShowPermissions, setShouldShowPermissions] = useState(false);
  const [filterType, setFilterType] = useState<string | undefined>(undefined);
  const [visibleMetrics, setVisibleMetrics] = useState(mappedProps.metrics);
  const [podOptionsState, setPodOptions] = useState([]);

  useEffect(() => {
    devLogging("get metrics in home ", mappedProps.metrics);
    setVisibleMetrics(mappedProps.metrics);
  }, [mappedProps.metrics]);

  useEffect(() => {
    const fetchPodOptions = async () => {
      // @ts-ignore
      const options = await getPodOptions(mappedProps.userRoles?.payload, selectedCity);
      setPodOptions(options);
    };
    fetchPodOptions();
  }, [mappedProps.userRoles?.payload?.pods, mappedProps.userRoles?.payload?.podNames, selectedCity]);

  // Add new state for restaurant data
  const [restaurantData, setRestaurantData] = useState<Array<{ restaurant_id: string; restaurant_name: string }>>([]);

  // Add effect to fetch restaurant data
  useEffect(() => {
    const getRestaurantData = async () => {
      try {
        const data = await fetchRestaurantData();
        setRestaurantData(data);
      } catch (error) {
        console.error("Error getting restaurant data:", error);
      }
    };

    getRestaurantData();
  }, []);

  // Update the restaurantOptions to use actual names instead of just IDs
  const restaurantOptions = useMemo(() => {
    const restaurants = mappedProps.userRoles?.payload?.restaurantIds || [];

    return restaurants.map((id) => {
      // Find the restaurant name from our fetched data
      const restaurantInfo = restaurantData.find((r) => r.restaurant_id === id);

      return {
        value: id,
        label: restaurantInfo ? `${restaurantInfo.restaurant_name} (${id})` : `Restaurant ${id}`, // Fallback in case name isn't found
      };
    });
  }, [mappedProps.userRoles?.payload?.restaurantIds, restaurantData]);

  const handleRestaurantChange = useCallback((value: string[]) => {
    setSelectedRestaurantId(value);
  }, []);

  // Add state for POC data
  const [pocData, setPocData] = useState<string[]>([]);

  // Add effect to fetch POC data
  useEffect(() => {
    const getPocData = async () => {
      try {
        const data = await fetchPocData();
        setPocData(data);
      } catch (error) {
        console.error("Error getting POC data:", error);
      }
    };

    const getL1ManagerData = async () => {
      try {
        const data = await fetchL1ManagerData();
        setL1ManagerData(data);
      } catch (error) {
        console.error("Error getting L1 manager data:", error);
      }
    };

    getPocData();
    getL1ManagerData();
  }, []);

  // Update the pocEmailOptions to use actual data from API
  const pocEmailOptions = useMemo(() => {
    return pocData.map((email) => ({
      value: email,
      label: email,
    }));
  }, [pocData]);

  // Create L1 manager options
  const l1ManagerOptions = useMemo(() => {
    return l1ManagerData
      .filter(email => email) // Filter out empty strings
      .map((email) => ({
        value: email,
        label: email,
      }));
  }, [l1ManagerData]);

  // Add state for selected L1 manager
  const [selectedL1Manager, setSelectedL1Manager] = useState<string[] | undefined>(undefined);
  const [tempL1Manager, setTempL1Manager] = useState<string[] | undefined>(undefined);

  // Update the filter construction to include l1_manager_email_id
  const getFilters = useCallback(() => {
    // For L2 managers, return city and L1 manager filters
    if (isL2(mappedProps.userRoles?.payload?.misRole)) {
      return [
        ...(selectedCity ? [{ field: "city", value: selectedCity }] : []),
        ...(selectedPocEmailId ? [{ field: "poc_email_id", value: selectedPocEmailId }] : []),
        ...(selectedL1Manager && selectedL1Manager.length > 0 ? [{ field: "l1_manager_email_id", value: selectedL1Manager }] : [])
      ];
    }

    // For non-L2 managers, return all filters except L1 manager
    return [
      ...(selectedCity ? [{ field: "city", value: selectedCity }] : []),
      ...(selectedRestaurantId && selectedRestaurantId.length > 0
        ? [{ field: "restaurant_id", value: selectedRestaurantId }]
        : []),
      ...(selectedPocEmailId ? [{ field: "poc_email_id", value: selectedPocEmailId }] : []),
      ...(getServiceName() === ServiceName.ANOBIS_INDIA_2 &&
        (!selectedRestaurantId || selectedRestaurantId.length === 0)
        ? [{ field: "restaurant_id", value: mappedProps.userRoles.payload?.restaurantIds || [] }]
        : []),
    ];
  }, [selectedCity, selectedRestaurantId, selectedPocEmailId, selectedL1Manager, mappedProps.userRoles.payload]);

  // Update onRefresh to use getFilters
  const onRefresh = useCallback(() => {
    setIsRefreshClicked(Date.now());

    setTimeout(() => {
      setPage(1);
      mappedProps.fetchHome({ pageNo: 1, fresh: true, filters: getFilters() });
      // mappedProps.fetchAllOrgMetrics({ fresh: true });
    }, CALL_DELAY);
  }, [selectedPod, selectedCity, selectedRestaurantId, selectedPocEmailId, mappedProps, getFilters]);

  //@ts-ignore
  const cityOptions = useMemo(
    //@ts-ignore
    () => getCityOptions(mappedProps.userRoles?.payload),
    [mappedProps.userRoles?.payload?.cities]
  );

  const handleCityChange = useCallback(
    (value: string[]) => {
      setSelectedCity(value);
      // on city change, reset the selected pod
      setSelectedPod([]);
    },
    [mappedProps.userRoles.payload?.pods, mappedProps.userRoles.payload?.cities]
  );

  useEffect(() => {
    if (
      isRefreshClicked &&
      Date.now() - isRefreshClicked > CALL_DELAY &&
      safeGet(mappedProps, "chartsDataPending") === false
    ) {
      setIsRefreshClicked(0);
    }
  }, [safeGet(mappedProps, "chartsDataPending"), isRefreshClicked]);

  const onSearchClick = useCallback(() => {
    mappedProps.searchReset();
    toggleSearch(true);
  }, []);

  const closeSearch = useCallback(() => {
    mappedProps.searchReset();
    toggleSearch(false);
    setTargetSearch(SearchTarget.HOME);
  }, []);

  const handleChangeTab = useCallback(
    ({ label }: { label: string }) => {
      setPage(1);
      mappedProps.changeTimeTabAppWide(label);
      setTimeTabForImCom(label);

      // Fetch data with current filters when tab changes
      const currentFilters = getFilters();
      mappedProps.fetchHome({ pageNo: 1, fresh: true, filters: currentFilters });
    },
    [mappedProps, getFilters]
  );

  // persist the selected tab
  useEffect(() => {
    const savedTab = mappedProps.selectedTab;
    devLogging("saved a tab from the world ", savedTab);
    if (RunningAnobisMode.INDIA_2_MIS_TIME_TAB.includes(savedTab)) {
      // if selectedTab is in the list of time tabs for IMCOM
      setTimeTabForImCom(savedTab);
      mappedProps.changeTimeTabAppWide(savedTab);
    } else {
      mappedProps.changeTimeTabAppWide(timeTabForImCom);
      handleChangeTab({ label: timeTabForImCom }); // set the default tab
    }
  }, [timeTabForImCom, mappedProps]);

  const [skipEffectOnce, setSkipEffectOnce] = useState(false);
  const isFilterChangeInProgress = useRef(false);

  const handleFilterModalOk = () => {
    // Batch all state updates together
    const newFilters = {
      city: tempCity,
      restaurantId: isL2(mappedProps.userRoles?.payload?.misRole) ? undefined : tempRestaurantId,
      pocEmailId: tempPocEmailId,
      l1Manager: isL2(mappedProps.userRoles?.payload?.misRole) ? tempL1Manager : undefined
    };

    // Create API filters array
    const apiFilters = [
      ...(tempCity && tempCity.length > 0 ? [{ field: "city", value: tempCity }] : []),
      ...(!isL2(mappedProps.userRoles?.payload?.misRole) && tempRestaurantId && tempRestaurantId.length > 0
        ? [{ field: "restaurant_id", value: tempRestaurantId }]
        : []),
      ...(tempPocEmailId && tempPocEmailId.length > 0 ? [{ field: "poc_email_id", value: tempPocEmailId }] : []),
      ...(isL2(mappedProps.userRoles?.payload?.misRole) && tempL1Manager && tempL1Manager.length > 0
        ? [{ field: "l1_manager_email_id", value: tempL1Manager }]
        : [])
    ];

    // If no filters are selected and we're in ANOBIS_INDIA_2 mode, use default filters based on role
    if (apiFilters.length === 0 && getServiceName() === ServiceName.ANOBIS_INDIA_2) {
      if (isL2(mappedProps.userRoles?.payload?.misRole)) {
        apiFilters.push({ field: "city", value: mappedProps.userRoles.payload.cities });
      } else if (mappedProps.userRoles?.payload?.restaurantIds) {
        apiFilters.push({ field: "restaurant_id", value: mappedProps.userRoles.payload.restaurantIds });
      }
    }

    // Set flag to prevent effect triggers
    isFilterChangeInProgress.current = true;

    // Batch all state updates
    setSelectedCity(tempCity);
    setSelectedRestaurantId(isL2(mappedProps.userRoles?.payload?.misRole) ? undefined : tempRestaurantId);
    setSelectedPocEmailId(tempPocEmailId);
    setSelectedL1Manager(isL2(mappedProps.userRoles?.payload?.misRole) ? tempL1Manager : undefined);
    setIsFilterActive(!!(
      (tempCity && tempCity.length > 0) ||
      (!isL2(mappedProps.userRoles?.payload?.misRole) && tempRestaurantId && tempRestaurantId.length > 0) ||
      (tempPocEmailId && tempPocEmailId.length > 0) ||
      (isL2(mappedProps.userRoles?.payload?.misRole) && tempL1Manager && tempL1Manager.length > 0)
    ));
    setPage(1);
    setIsFilterModalVisible(false);

    // Save filters to localStorage
    localStorage.setItem("india2mis_filters", JSON.stringify(newFilters));

    // Fetch data with new filters
    mappedProps.fetchHome({ pageNo: 1, fresh: true, filters: apiFilters });

    // Reset the flag after a short delay
    setTimeout(() => {
      isFilterChangeInProgress.current = false;
    }, 100);
  };

  // Optimize the filter effect
  useEffect(() => {
    if (isFilterChangeInProgress.current) return;

    // Skip initial render
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    // Skip if we just applied filters
    if (skipEffectOnce) {
      setSkipEffectOnce(false);
      return;
    }

    // Only fetch if we have actual filter changes
    const hasFilterChanges = selectedCity || (selectedRestaurantId && selectedRestaurantId.length > 0) || selectedPocEmailId || selectedL1Manager;
    if (hasFilterChanges) {
      setPage(1);
      mappedProps.fetchHome({ pageNo: 1, fresh: true, filters: getFilters() });
      // mappedProps.fetchAllOrgMetrics({ fresh: true });
    }
  }, [selectedCity, selectedRestaurantId, selectedPocEmailId, selectedL1Manager]);

  // Modify the initial load effect to properly apply saved filters
  useEffect(() => {
    const savedFilters = localStorage.getItem("india2mis_filters");
    if (savedFilters) {
      try {
        const { city, restaurantId, pocEmailId, l1Manager } = JSON.parse(savedFilters);

        // Set flag to prevent effect triggers
        isFilterChangeInProgress.current = true;

        // Batch state updates
        setSelectedCity(city);
        setSelectedRestaurantId(restaurantId);
        setSelectedPocEmailId(pocEmailId);
        setSelectedL1Manager(l1Manager ? (Array.isArray(l1Manager) ? l1Manager : [l1Manager]) : undefined);
        setTempCity(city);
        setTempRestaurantId(restaurantId);
        setTempPocEmailId(pocEmailId);
        setTempL1Manager(l1Manager ? (Array.isArray(l1Manager) ? l1Manager : [l1Manager]) : undefined);
        setIsFilterActive(!!(
          (city && city.length > 0) ||
          (restaurantId && restaurantId.length > 0) ||
          (pocEmailId && pocEmailId.length > 0) ||
          (l1Manager && (Array.isArray(l1Manager) ? l1Manager.length > 0 : !!l1Manager))
        ));

        // Create API filters array
        const apiFilters = [
          ...(city && city.length > 0 ? [{ field: "city", value: city }] : []),
          ...(restaurantId && restaurantId.length > 0 ? [{ field: "restaurant_id", value: restaurantId }] : []),
          ...(pocEmailId && pocEmailId.length > 0 ? [{ field: "poc_email_id", value: pocEmailId }] : []),
          ...(l1Manager && l1Manager.length > 0 ? [{ field: "l1_manager_email_id", value: l1Manager }] : [])
        ];

        // If no filters are selected and we're in ANOBIS_INDIA_2 mode, use default restaurant IDs
        if (apiFilters.length === 0 && getServiceName() === ServiceName.ANOBIS_INDIA_2 && mappedProps.userRoles?.payload?.restaurantIds) {
          if (isL2(mappedProps.userRoles.payload.misRole)) {
            apiFilters.push({ field: "city", value: mappedProps.userRoles.payload.cities });
          } else {
            apiFilters.push({ field: "restaurant_id", value: mappedProps.userRoles.payload.restaurantIds });
          }
        }

        // Fetch data with the saved filters
        mappedProps.fetchHome({ pageNo: 1, fresh: true, filters: apiFilters });
        // mappedProps.fetchAllOrgMetrics({ fresh: true });

        // Reset the flag after a short delay
        setTimeout(() => {
          isFilterChangeInProgress.current = false;
        }, 100);
      } catch (error) {
        console.error("Error loading filters from localStorage:", error);
      }
    } else {
      // If no saved filters, fetch with default filters
      let defaultFilters: Filter[] = [];
      if (getServiceName() === ServiceName.ANOBIS_INDIA_2 && mappedProps.userRoles?.payload?.restaurantIds) {
        if (isL2(mappedProps.userRoles.payload.misRole)) {
          defaultFilters = [{ field: "city", value: mappedProps.userRoles.payload.cities }];
        } else {
          defaultFilters = [{ field: "restaurant_id", value: mappedProps.userRoles.payload.restaurantIds }];
        }
      }

      mappedProps.fetchHome({ pageNo: 1, fresh: true, filters: defaultFilters });
      // mappedProps.fetchAllOrgMetrics({ fresh: true });
    }

    // Reset other states
    mappedProps.setCategoriesFilters({});
    mappedProps.categoryReset();
    setL3Fiters({});
    clearL3Data();
    setSelectedPod([]);
    setPage(1);
  }, []); // Run only on mount

  // Remove the duplicate effect that was causing issues
  useEffect(() => {
    if (safeGet(mappedProps, "comingBackFromDetailsPage") === true) {
      mappedProps.setComingBackFromDetailsPage(false);
      if (safeGet(mappedProps, "metrics.success") === false) {
        mappedProps.fetchHome({ pageNo: 1, fresh: false, filters: getFilters() });
      }
    }
  }, [safeGet(mappedProps, "comingBackFromDetailsPage")]);

  useEffect(() => {
    const currentDiv = containerRef.current;
    currentDiv?.addEventListener("scroll", () => {
      localStorage.setItem("AnobisYscrollPositionPinnedPage", currentDiv?.scrollTop.toString());
    });
    if (mappedProps.metrics.success) {
      const scrollPosition: any = localStorage.getItem("AnobisYscrollPositionPinnedPage");
      currentDiv?.scrollTo({
        top: parseInt(scrollPosition),
        behavior: "smooth",
      });
    }
  });

  // set the default city if there is only one city
  useEffect(() => {
    if (cityOptions.length === 1) {
      setSelectedCity([cityOptions[0].value]);
      handleCityChange([cityOptions[0].value]);
    }
  }, [cityOptions, mappedProps.selectedTab]);

  // Update loading state based on metrics loading status
  useEffect(() => {
    const isMetricsPending = safeGet(mappedProps, "metrics.pending", true);
    const hasMetricsSuccess = safeGet(mappedProps, "metrics.success", false);

    if (!isMetricsPending && hasMetricsSuccess) {
      // Add a small delay to ensure UI renders properly before enabling interaction
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    } else {
      setIsLoading(true);
    }
  }, [safeGet(mappedProps, "metrics.pending"), safeGet(mappedProps, "metrics.success")]);

  const options = useMemo(
    () => [
      {
        className: "",
        iconClass: IconClass.SEARCH,
        handleClick: onSearchClick,
      },
      {
        className: "",
        iconClass: IconClass.COMPARE,
        handleClick: () => history.push("/metric-comparison"),
      },
    ],
    [onSearchClick]
  );

  const searchMetric = (text) => {
    mappedProps.searchPin({ text, targetSearch });
  };

  const meticOptionsV1 = useMemo(() => {
    return mappedProps.metrics.payload?.data.map((metric) => ({
      value: metric.label,
      label: metric.label,
    }));
  }, [mappedProps.metrics]);

  // Add state to control modal visibility
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);

  // Add these near other state declarations
  const [tempCity, setTempCity] = useState<string[] | undefined>(undefined);
  const [tempRestaurantId, setTempRestaurantId] = useState<string[] | undefined>(undefined);
  const [tempPocEmailId, setTempPocEmailId] = useState<string[] | undefined>(undefined);
  const [isFilterActive, setIsFilterActive] = useState(false);

  // Update the showFilterModal function
  const showFilterModal = () => {
    // Initialize temp values with current filter values
    setTempCity(selectedCity);
    setTempRestaurantId(selectedRestaurantId);
    setTempPocEmailId(selectedPocEmailId);
    setTempL1Manager(selectedL1Manager);
    setIsFilterModalVisible(true);
  };

  const handleFilterModalCancel = () => {
    // Reset temp values to current filter values
    setTempCity(selectedCity);
    setTempRestaurantId(selectedRestaurantId);
    setTempPocEmailId(selectedPocEmailId);
    setTempL1Manager(selectedL1Manager);
    setIsFilterModalVisible(false);
  };

  const isInitialRender = useRef(true);

  const loadMore = useCallback(() => {
    if (isFilterChangeInProgress.current) return;

    if (
      !(safeGet(mappedProps, "metrics.pending", false) || safeGet(mappedProps, "loadMorePending", false)) &&
      safeGet(mappedProps, "metrics.payload.hasMore", false)
    ) {
      mappedProps.fetchHome({ pageNo: page + 1, filters: getFilters() });
      setPage(page + 1);
    }
  }, [mappedProps.metrics, safeGet(mappedProps, "loadMorePending"), page, getFilters]);

  // Add clear filters function
  const clearAllFilters = useCallback(() => {
    // Set flag to prevent effect triggers
    isFilterChangeInProgress.current = true;

    // Clear all filter states
    setSelectedCity(undefined);
    setSelectedRestaurantId(undefined);
    setSelectedPocEmailId(undefined);
    setSelectedL1Manager(undefined);
    setTempCity(undefined);
    setTempRestaurantId(undefined);
    setTempPocEmailId(undefined);
    setTempL1Manager(undefined);
    setIsFilterActive(false);

    // Clear localStorage
    localStorage.removeItem("india2mis_filters");

    // Create default filters for ANOBIS_INDIA_2 mode
    let defaultFilters: Filter[] = [];
    if (getServiceName() === ServiceName.ANOBIS_INDIA_2 && mappedProps.userRoles?.payload?.restaurantIds) {
      if (isL2(mappedProps.userRoles.payload.misRole)) {
        defaultFilters = [{ field: "city", value: mappedProps.userRoles.payload.cities }];
      } else {
        defaultFilters = [{ field: "restaurant_id", value: mappedProps.userRoles.payload.restaurantIds }];
      }
    }

    // Fetch data with default filters
    mappedProps.fetchHome({ pageNo: 1, fresh: true, filters: defaultFilters });
    // mappedProps.fetchAllOrgMetrics({ fresh: true });

    // Reset the flag after a short delay
    setTimeout(() => {
      isFilterChangeInProgress.current = false;
    }, 100);
  }, [mappedProps.userRoles?.payload?.restaurantIds]);

  return (
    <>
      {isLoading && (
        <LoadingOverlay>
          <SpinnerContainer>
            <PulseRing />
            <LoadingSpinner />
            <InnerSpinner />
            <CenterPoint />
          </SpinnerContainer>
          <LoadingText>Loading Your Data...</LoadingText>
        </LoadingOverlay>
      )}
      {!manageMetricsFlag && (
        <animated.section
          className={`${styles["container"]} ${showRibbon ? styles.overlayOpen : ""} ${showSearch ? styles["display-none"] : ""
            }`}
          style={{ position: "absolute" }}
          ref={(target) => {
            containerRef.current = target as HTMLDivElement;
          }}
        >
          {showRibbon && safeGet(mappedProps, "metrics.success", false) ? (
            <SliderContainer>
              <RibbonSlider height={DRAGPANEL_TOP_THRESHOLD} pins={safeGet(mappedProps, "metrics.payload", [])} />
            </SliderContainer>
          ) : null}
          <div className={styles["home-top-nav"]} style={{ opacity: showRibbon ? 0 : 1 }}>
            <TopNavbar
              handleClick={noop}
              headerText=""
              isHome
              onLogout={onLogout}
              onPermissionsShow={() => setShouldShowPermissions(true)}
            >
              <>
                {isFilterActive && (
                  <Button
                    type="primary"
                    onClick={clearAllFilters}
                    style={{
                      marginRight: 10,
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: colors.downRed,
                      borderColor: colors.downRed,

                    }}
                  >
                    Clear Filters
                  </Button>
                )}
                <Button
                  icon={isFilterActive ? <FilterFilled /> : <FilterOutlined />}
                  onClick={showFilterModal}
                  style={{
                    marginRight: 10,
                    borderColor: isFilterActive ? colors.primaryColor : undefined,
                    color: isFilterActive ? colors.primaryColor : undefined,
                  }}
                >
                  Filters
                </Button>

                {safeGet(mappedProps, "chartsDataPending", false) || !!isRefreshClicked ? (
                  <SyncIcon spin style={{ color: "blue" }} />
                ) : (
                  <SyncIcon onClick={onRefresh} style={{ color: "black" }} />
                )}
              </>
            </TopNavbar>

            {/* Filter Chips */}
            {isFilterActive && (
              <FilterChipsContainer>
                {selectedCity && selectedCity.length > 0 && (
                  <FilterChip>{selectedCity.length > 1 ? `Cities (${selectedCity.length})` : `City: ${selectedCity[0]}`}</FilterChip>
                )}
                {selectedPocEmailId && selectedPocEmailId.length > 0 && (
                  <FilterChip>
                    {selectedPocEmailId.length > 1
                      ? `POCs (${selectedPocEmailId.length})`
                      : `POC: ${selectedPocEmailId[0].split('@')[0]}`}
                  </FilterChip>
                )}
                {selectedL1Manager && selectedL1Manager.length > 0 && (
                  <FilterChip>
                    {selectedL1Manager.length > 1
                      ? `L1 Managers (${selectedL1Manager.length})`
                      : `L1: ${selectedL1Manager[0].split('@')[0]}`}
                  </FilterChip>
                )}
                {selectedRestaurantId && selectedRestaurantId.length > 0 && (
                  <FilterChip>Restaurants: {selectedRestaurantId.length}</FilterChip>
                )}
              </FilterChipsContainer>
            )}
          </div>

          <div className={styles["pinned-items"]} style={{ opacity: showRibbon ? 0 : 1 }}>
            <PinnedContainer
              isPulledUp={showRibbon}
              pins={visibleMetrics}
              showDetails={showDetails}
              isPending={safeGet(mappedProps, "metrics.pending", false)}
              onChangeTab={handleChangeTab}
              switchToManageMetrics={setManageMetricsFlag}
              selectedTab={mappedProps.selectedTab}
              brokenPodsVisibleTabs={RunningAnobisMode.BROKEN_RESTAURANTS_VISIBLE_TIME_TAB}
            />
          </div>

          {safeGet(mappedProps, "metrics.payload.hasMore", false) && (
            <Button type="primary" shape="round" className={styles["load-more-button"]} onClick={loadMore}>
              Load more
            </Button>
          )}
        </animated.section>
      )}
      {manageMetricsFlag && (
        <>
          <ManageMetrics refreshHome={onRefresh} switchBackToHome={setManageMetricsFlag} />
        </>
      )}
      {showSearch && (
        <Search
          closeSearch={closeSearch}
          searchMetric={searchMetric}
          searchResult={safeGet(mappedProps, "search.payload", []) || []}
          open={showSearch}
          target={targetSearch}
          searchSuccess={safeGet(mappedProps, "search.success", false)}
          searchPending={safeGet(mappedProps, "search.pending", false)}
          resetSearch={mappedProps.searchReset}
        />
      )}
      {console.log("logging user roles here for you for all of us ", mappedProps.userRoles)}
      {shouldShowPermissions && (
        <Permissions roles={mappedProps.userRoles} onClose={() => setShouldShowPermissions(false)} />
      )}

      {/* Filter Modal */}
      <Modal
        title="Filter Options"
        visible={isFilterModalVisible}
        onOk={handleFilterModalOk}
        onCancel={handleFilterModalCancel}
        footer={[
          <Button key="cancel" onClick={handleFilterModalCancel}>
            Cancel
          </Button>,
          <Button key="apply" type="primary" onClick={handleFilterModalOk}>
            Apply Filters
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          {/* L2 managers see L1 manager filter first */}
          {isL2(mappedProps.userRoles?.payload?.misRole) && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8 }}>L1 Manager</label>
              {l1ManagerOptions.length > 0 && (
                <Select
                  placeholder="Filter by L1 Manager"
                  style={{ width: "100%", borderWidth: "0.02rem", borderStyle: "solid" }}
                  value={tempL1Manager}
                  onChange={(value) => setTempL1Manager(value)}
                  options={l1ManagerOptions}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label?.toString() || "").toLowerCase().includes(input.toLowerCase())
                  }
                  listHeight={200}
                  onClear={() => setTempL1Manager(undefined)}
                  mode="multiple"
                />
              )}
            </div>
          )}

          {/* POC Email filter */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 8 }}>POC Email</label>
            {pocEmailOptions.length > 0 && (
              <Select
                mode="multiple"
                placeholder="Filter by POC Email"
                style={{ width: "100%", borderWidth: "0.02rem", borderStyle: "solid" }}
                value={tempPocEmailId}
                onChange={(value) => setTempPocEmailId(value)}
                options={pocEmailOptions}
                allowClear
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label?.toString() || "").toLowerCase().includes(input.toLowerCase())
                }
                listHeight={200}
                maxTagCount={3}
                onClear={() => setTempPocEmailId(undefined)}
              />
            )}
          </div>

          {/* City filter */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 8 }}>City</label>
            {cityOptions.length > 0 && (
              <Select
                mode="multiple"
                placeholder="Filter by City"
                style={{ width: "100%", borderWidth: "0.02rem", borderStyle: "solid" }}
                value={tempCity}
                onChange={(value) => setTempCity(value)}
                options={cityOptions}
                showSearch
                allowClear
                listHeight={200}
                maxTagCount={3}
                onClear={() => setTempCity(undefined)}
              />
            )}
          </div>

          {/* Restaurant ID filter - only shown for non-L2 managers */}
          {!isL2(mappedProps.userRoles?.payload?.misRole) && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8 }}>Restaurant</label>
              {restaurantOptions.length > 0 && (
                <Select
                  mode="multiple"
                  showSearch
                  placeholder="Filter by Restaurant"
                  style={{ width: "100%", borderWidth: "0.02rem", borderStyle: "solid" }}
                  value={tempRestaurantId}
                  onChange={(value) => setTempRestaurantId(value)}
                  options={restaurantOptions}
                  allowClear
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label?.toString() || "").toLowerCase().includes(input.toLowerCase())
                  }
                  listHeight={200}
                  maxTagCount={3}
                  onClear={() => setTempRestaurantId(undefined)}
                />
              )}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default MainContainer;
